import React, { useState, useEffect, useCallback } from 'react';
import { Button, Form, Table, Modal, Badge } from 'react-bootstrap';
import { getMyVisitsAll, getKgInfo, getTimeLine, getMyVisits, saveDate, delDate, fmtDateTime, DAYS_UK } from './activity';
import AutocompleteSelect from '../wizplain/components/Autocomplete/AutocompleteSelect';
import PhoneInput from '../wizplain/widgets/phone';
import '../MessagesContent.css';

// ─────────────────────────────────────────────
// Допоміжні
// ─────────────────────────────────────────────


const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString('uk-UA') : '—';



const visitStatus = (visit) => {
    if (visit?.result?.declined) return { label: 'Скасовано', variant: 'danger' };
    if (new Date(visit?.date) < new Date()) return { label: 'Відбувся', variant: 'secondary' };
    return { label: 'Очікується', variant: 'success' };
};

// ─────────────────────────────────────────────
// Модальне вікно деталей запису
// ─────────────────────────────────────────────

function DetailModal({ show, onHide, item, onCancel }) {
    if (!item) return null;
    const { kindergartenNumber, kindergartenName, visit } = item;
    const status = visitStatus(visit);
    const canCancel = status.label === 'Очікується';

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Деталі запису на прийом</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mc-detail-body" style={{ maxHeight: 'none', overflow: 'visible', padding: 0 }}>
                    <table className="table table-bordered table-sm mb-0">
                        <tbody>
                            <tr>
                                <th style={{ width: '35%' }}>Садочок</th>
                                <td>№{kindergartenNumber} {kindergartenName}</td>
                            </tr>
                            <tr>
                                <th>Дата та час прийому</th>
                                <td>{fmtDateTime(visit?.date)}</td>
                            </tr>
                            <tr>
                                <th>Контактне ім'я</th>
                                <td>{visit?.contacts?.name || '—'}</td>
                            </tr>
                            <tr>
                                <th>Телефон</th>
                                <td>{visit?.contacts?.phone || '—'}</td>
                            </tr>
                            <tr>
                                <th>Мета візиту</th>
                                <td>{visit?.type_vis?.target || '—'}</td>
                            </tr>
                            <tr>
                                <th>Статус</th>
                                <td>
                                    <Badge bg={status.variant}>{status.label}</Badge>
                                </td>
                            </tr>
                            {visit?.comment_kg?.text && (
                                <tr>
                                    <th>Коментар садочку</th>
                                    <td>{visit.comment_kg.text}</td>
                                </tr>
                            )}
                            <tr>
                                <th>Дата подачі</th>
                                <td>{fmtDate(visit?.createdAt)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </Modal.Body>
            <Modal.Footer>
                {canCancel && (
                    <Button
                        variant="outline-danger"
                        onClick={() => onCancel(item)}
                    >
                        <i className="fa-solid fa-xmark me-2" />Скасувати запис
                    </Button>
                )}
                <Button variant="secondary" onClick={onHide}>Закрити</Button>
            </Modal.Footer>
        </Modal>
    );
}

// ─────────────────────────────────────────────
// Wizard — новий запис на прийом
// ─────────────────────────────────────────────

function CreateVisitModal({ show, onHide, onCreated }) {
    const [kgsList, setKgsList]       = useState([]);
    const [kgId, setKgId]             = useState('');
    const [timeline, setTimeline]     = useState({});
    const [curDay, setCurDay]         = useState('');
    const [curTime, setCurTime]       = useState('');
    const [myVisit, setMyVisit]       = useState(null);
    const [contacts, setContacts]     = useState({ name: '', phone: '' });
    const [errors, setErrors]         = useState({});
    const [loadingTl, setLoadingTl]   = useState(false);
    const [sending, setSending]       = useState(false);

    // Завантаження списку садочків
    useEffect(() => {
        if (!show) return;
        const api = async () => {
            try {
                const data = await getKgInfo();
                if (data) setKgsList(data);
            } catch (_) {}
        };
        api();
    }, [show]);

    // Скидання при закритті
    useEffect(() => {
        if (!show) {
            setKgId(''); setTimeline({}); setCurDay(''); setCurTime('');
            setMyVisit(null); setErrors({}); setContacts({ name: '', phone: '' });
        }
    }, [show]);

    // Завантаження таймлайну та поточного запису при зміні садочку
    useEffect(() => {
        if (!kgId) { setTimeline({}); setCurDay(''); setCurTime(''); setMyVisit(null); return; }
        const api = async () => {
            setLoadingTl(true);
            try {
                const [tl, visits] = await Promise.all([getTimeLine(kgId), getMyVisits(kgId)]);
                const tlObj = tl || {};
                setTimeline(tlObj);
                const days = Object.keys(tlObj).sort();
                if (days.length) {
                    setCurDay(days[0]);
                    setCurTime(tlObj[days[0]][0] || '');
                } else {
                    setCurDay(''); setCurTime('');
                }
                const existingVisit = visits?.[0]?.visits?.[0] || null;
                setMyVisit(existingVisit);
            } catch (_) {
                setTimeline({}); setCurDay(''); setCurTime('');
            } finally {
                setLoadingTl(false);
            }
        };
        api();
    }, [kgId]);

    const kgOptions = kgsList.map(kg => ({
        _id: kg.garten_pdo_id,
        name: `№${kg.garten_number} ${kg.garten_name}`,
        description: kg.garten_address || '',
    }));

    const sortedDays = Object.keys(timeline).sort();

    const dayOptions = sortedDays.map(d => ({
        _id: d,
        name: `${new Date(d).toLocaleDateString('uk-UA')}, ${DAYS_UK[new Date(d).getDay()]}`,
    }));

    const timeOptions = (curDay && timeline[curDay])
        ? timeline[curDay].map(t => ({ _id: t, name: t }))
        : [];

    const handleDayChange = (id) => {
        setCurDay(id);
        const times = timeline[id] || [];
        setCurTime(times[0] || '');
        setErrors(prev => ({ ...prev, date: undefined, time: undefined }));
    };

    const handleTimeChange = (id) => {
        setCurTime(id);
        setErrors(prev => ({ ...prev, time: undefined }));
    };

    const handleSave = async () => {
        const newErrors = {};
        if (!kgId) newErrors.kg_id = 'Оберіть садочок';
        if (!curDay) newErrors.date = 'Оберіть дату';
        if (!curTime) newErrors.time = 'Оберіть час';
        if (!contacts.name.trim()) newErrors.name = 'Вкажіть контактне ім\'я';
        if (Object.keys(newErrors).length) { setErrors(newErrors); return; }

        setSending(true);
        try {
            const dateStr = `${curDay}T${curTime.length === 4 ? '0' : ''}${curTime}`;
            await saveDate({ kg_id: kgId, date: dateStr, contacts });
            onCreated && onCreated();
            onHide();
        } catch (_) {
            alert('Помилка при збереженні. Спробуйте ще раз.');
        } finally {
            setSending(false);
        }
    };

    const handleCancel = async () => {
        if (!window.confirm('Ви дійсно бажаєте скасувати запис до директора садочка?')) return;
        try {
            await delDate({ kg_id: kgId, date: `${curDay}T${curTime}` });
            setMyVisit(null);
            onCreated && onCreated();
        } catch (_) {}
    };

    const noSlots = kgId && !loadingTl && Object.keys(timeline).length === 0 && !myVisit;

    return (
        <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="fa-solid fa-calendar-check me-2" />Новий запис на прийом до директора
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className="mc-modal-body-fix">
                    <div className="mc-wizard-step">
                        <p className="mc-wizard-hint">
                            Оберіть садочок та зручний час для зустрічі з директором.
                        </p>

                        {/* Вибір садочку */}
                        <Form.Group className="mb-3">
                            <AutocompleteSelect
                                value={kgId}
                                onChange={(id) => { setKgId(id); setErrors(prev => ({ ...prev, kg_id: undefined })); }}
                                options={kgOptions}
                                placeholder="Почніть вводити назву садочка..."
                                label="Оберіть садочок *"
                                error={errors.kg_id}
                                fieldsValue={['name']}
                            />
                        </Form.Group>

                        {/* Спінер під час завантаження таймлайну */}
                        {loadingTl && (
                            <div className="text-center py-3 text-muted">
                                <span className="spinner-border spinner-border-sm me-2" />
                                Завантаження доступних дат...
                            </div>
                        )}

                        {/* Немає слотів */}
                        {noSlots && (
                            <div className="alert alert-warning py-2">
                                На жаль, у обраному садочку немає доступних дат для запису.
                            </div>
                        )}

                        {/* Вже є запис */}
                        {myVisit && !loadingTl && (
                            <div>
                                <div className="alert alert-info py-2 mb-3">
                                    У вас вже є запис у цей садочок.
                                </div>
                                <table className="table table-sm table-bordered mb-3">
                                    <tbody>
                                        <tr>
                                            <th>Дата та час</th>
                                            <td>{fmtDateTime(myVisit.date)}</td>
                                        </tr>
                                        <tr>
                                            <th>Статус</th>
                                            <td>
                                                <Badge bg={visitStatus(myVisit).variant}>
                                                    {visitStatus(myVisit).label}
                                                </Badge>
                                            </td>
                                        </tr>
                                        {myVisit?.comment_kg?.text && (
                                            <tr>
                                                <th>Коментар</th>
                                                <td>{myVisit.comment_kg.text}</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                                <Button variant="outline-danger" size="sm" onClick={handleCancel}>
                                    <i className="fa-solid fa-xmark me-2" />Скасувати запис
                                </Button>
                            </div>
                        )}

                        {/* Форма вибору дати/часу */}
                        {!myVisit && !loadingTl && Object.keys(timeline).length > 0 && (
                            <div>
                                <Form.Group className="mb-3">
                                    <AutocompleteSelect
                                        value={curDay}
                                        onChange={handleDayChange}
                                        options={dayOptions}
                                        placeholder="Оберіть дату"
                                        label="Дата прийому *"
                                        error={errors.date}
                                        fieldsValue={['name']}
                                    />
                                </Form.Group>

                                {curDay && (
                                    <Form.Group className="mb-3">
                                        <AutocompleteSelect
                                            value={curTime}
                                            onChange={handleTimeChange}
                                            options={timeOptions}
                                            placeholder="Оберіть час"
                                            label="Час прийому *"
                                            error={errors.time}
                                            fieldsValue={['name']}
                                        />
                                    </Form.Group>
                                )}

                                <Form.Group className="mb-3">
                                    <Form.Label>Контактне ім'я *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Прізвище та ім'я"
                                        value={contacts.name}
                                        onChange={e => {
                                            setContacts(prev => ({ ...prev, name: e.target.value }));
                                            setErrors(prev => ({ ...prev, name: undefined }));
                                        }}
                                        isInvalid={!!errors.name}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.name}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    
                                    <PhoneInput 
            label="Контактний телефон" 
            value={contacts.phone} 
            onChange={(e) => setContacts(prev => ({ ...prev, phone: e.target.value }))} 
            //error={errors.applicantPhone}
            />
                                   
                                </Form.Group>
                            </div>
                        )}
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Скасувати</Button>
                {!myVisit && !loadingTl && Object.keys(timeline).length > 0 && (
                    <Button variant="primary" onClick={handleSave} disabled={sending}>
                        {sending
                            ? <><span className="spinner-border spinner-border-sm me-2" />Збереження...</>
                            : <><i className="fa-solid fa-calendar-plus me-2" />Записатись</>
                        }
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
}

// ─────────────────────────────────────────────
// Головний компонент
// ─────────────────────────────────────────────

const VisitsContent = ({ onInfo }) => {
    const [visits, setVisits]         = useState([]);
    const [loading, setLoading]       = useState(true);
    const [search, setSearch]         = useState('');

    // Модальні
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailItem, setDetailItem] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);

    const loadVisits = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getMyVisitsAll();
            // getMyVisitsAll повертає result.data з apiClient.
            // Залежно від того, як налаштований apiClient, це може бути:
            //   { data: [...] }  або одразу  [...]
            const data = Array.isArray(res) ? res : (res?.data || []);
            setVisits(data);
            if (onInfo) {
                const upcoming = data.filter(v => visitStatus(v.visit).label === 'Очікується');
                onInfo({ all: data.length, upcoming: upcoming.length });
            }
        } catch (_) {
            setVisits([]);
        } finally {
            setLoading(false);
        }
    }, [onInfo]);

    useEffect(() => { loadVisits(); }, []);

    // Фільтрація
    const filtered = visits.filter(item => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return (
            item.kindergartenName?.toLowerCase().includes(q) ||
            item.kindergartenNumber?.toString().includes(q) ||
            item.visit?.contacts?.name?.toLowerCase().includes(q) ||
            fmtDate(item.visit?.date).includes(q)
        );
    });

    // Скасування з детальної
    const handleCancelFromDetail = async (item) => {
        if (!window.confirm('Ви дійсно бажаєте скасувати запис до директора садочка?')) return;
        try {
            const visit = item.visit;
            const d = new Date(visit.date);
            const day  = d.toISOString().slice(0, 10);
            const time = d.toTimeString().slice(0, 5);
            await delDate({ kg_id: item.kindergartenId, date: `${day}T${time}` });
            setDetailOpen(false);
            loadVisits();
        } catch (_) {
            alert('Помилка при скасуванні.');
        }
    };

    return (
        <div className="messages-content">
            <div className="mc-content-wrapper">
                <h1 className="mc-page-title">Мої записи на прийом</h1>

                <div className="mc-toolbar">
                    <div className="mc-left-tools">
                        <Button className="mc-btn-new" onClick={() => setCreateOpen(true)}>
                            <i className="fa-solid fa-plus me-2" />Новий запис
                        </Button>
                    </div>
                    <div className="mc-right-tools">
                        <div className="mc-search-box">
                            <Form.Control
                                type="text"
                                className="mc-search-input"
                                placeholder="Швидкий пошук"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                            <i className="fa-solid fa-magnifying-glass" />
                        </div>
                    </div>
                </div>

                <div className="mc-table-wrapper">
                    {loading ? (
                        <div className="text-center py-5 text-muted">
                            <span className="spinner-border me-2" />Завантаження...
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            {search ? 'Нічого не знайдено' : 'Записів на прийом поки немає'}
                        </div>
                    ) : (
                        <Table className="mc-table">
                            <thead>
                                <tr>
                                    <th>Дата подачі</th>
                                    <th>Садочок</th>
                                    <th>Дата та час прийому</th>
                                    <th>Контакт</th>
                                    <th>Статус</th>
                                    <th>Коментар</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((item, idx) => {
                                    const { kindergartenNumber, kindergartenName, visit } = item;
                                    const status = visitStatus(visit);
                                    const comment = visit?.comment_kg?.text || '';
                                    return (
                                        <tr
                                            key={visit?._id || idx}
                                            className="mc-tr-hover"
                                            onClick={() => { setDetailItem(item); setDetailOpen(true); }}
                                        >
                                            <td data-label="Дата подачі">
                                                {fmtDate(visit?.createdAt)}
                                            </td>
                                            <td data-label="Садочок">
                                                №{kindergartenNumber} {kindergartenName}
                                            </td>
                                            <td data-label="Дата та час прийому">
                                                {fmtDateTime(visit?.date)}
                                            </td>
                                            <td data-label="Контакт">
                                                {visit?.contacts?.name || '—'}
                                            </td>
                                            <td data-label="Статус">
                                                <Badge bg={status.variant}>{status.label}</Badge>
                                            </td>
                                            <td data-label="Коментар" className="mc-answer-text">
                                                {comment
                                                    ? <span title={comment}>{comment.slice(0, 40)}{comment.length > 40 ? '…' : ''}</span>
                                                    : <span className="text-muted">—</span>
                                                }
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    )}
                </div>
            </div>

            {/* Деталі запису */}
            <DetailModal
                show={detailOpen}
                onHide={() => setDetailOpen(false)}
                item={detailItem}
                onCancel={handleCancelFromDetail}
            />

            {/* Новий запис */}
            <CreateVisitModal
                show={createOpen}
                onHide={() => setCreateOpen(false)}
                onCreated={loadVisits}
            />
        </div>
    );
};

export default VisitsContent;